import React, { useState, useEffect, Fragment } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { API_URL } from "../../constant";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import {
  MdCheckCircle,
  MdError,
  MdLock,
  MdStorefront,
  MdAccountBalanceWallet,
  MdQrCodeScanner,
  MdSearch,
} from "react-icons/md";
import "./ClaimTopCoinCard.css";

const ClaimTopCoinCard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isCustomerPortal = location.pathname.startsWith("/customer");

  const initialCard = searchParams.get("card") || searchParams.get("card_number") || "";
  const initialSerial = searchParams.get("serial") || searchParams.get("serial_number") || "";

  const [cardNumber, setCardNumber] = useState(initialCard);
  const [serialNumber, setSerialNumber] = useState(initialSerial);
  const [cardDetails, setCardDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [error, setError] = useState(null);
  const [claimResult, setClaimResult] = useState(null);

  const token = localStorage.getItem("auth_token");
  const isAuthenticated = Boolean(token);

  // If user is already logged in and views the public /claim-card link, redirect to customer portal
  useEffect(() => {
    if (isAuthenticated && !isCustomerPortal) {
      navigate(`/customer/claim-card${location.search}`, { replace: true });
    }
  }, [isAuthenticated, isCustomerPortal, location.search, navigate]);

  // Auto-fetch details if query parameters are present
  useEffect(() => {
    if (initialCard) {
      fetchCardDetails(initialCard, initialSerial);
    }
  }, [initialCard, initialSerial]);

  const fetchCardDetails = async (card, serial) => {
    if (!card) return;
    setLoading(true);
    setError(null);
    setClaimResult(null);

    try {
      const url = new URL(`${API_URL}/api/public/topcoin-card/details`);
      url.searchParams.append("card", card.trim());
      if (serial) {
        url.searchParams.append("serial", serial.trim());
      }

      const headers = { Accept: "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const res = await fetch(url.toString(), { headers });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Card not found or invalid details.");
      }

      setCardDetails(data.data);
    } catch (err) {
      console.error("Fetch card error:", err);
      setError(err.message || "Failed to load card information.");
      setCardDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (!cardNumber.trim()) {
      setError("Please enter a Card Number.");
      return;
    }
    fetchCardDetails(cardNumber, serialNumber);
  };

  const handleClaim = async () => {
    if (!isAuthenticated) {
      // Save return url so after login customer comes straight back
      sessionStorage.setItem("claim_card_pending", JSON.stringify({ card: cardNumber, serial: serialNumber }));
      navigate("/login");
      return;
    }

    if (!cardDetails) return;

    setClaimLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/customer/topcoin-card/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify({
          card: cardDetails.card_number,
          serial: serialNumber || undefined,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to claim TopCoin card.");
      }

      setClaimResult(data);
      // Update local card status to redeemed
      setCardDetails((prev) => ({
        ...prev,
        status: "redeemed",
        is_redeemed: true,
        can_claim: false,
      }));
    } catch (err) {
      console.error("Claim error:", err);
      setError(err.message || "Could not claim card reward.");
    } finally {
      setClaimLoading(false);
    }
  };

  const getTierClass = (tier) => {
    switch (tier?.toLowerCase()) {
      case "gold":
        return "card-tier-gold";
      case "red":
        return "card-tier-red";
      case "purple":
        return "card-tier-purple";
      case "brown":
        return "card-tier-brown";
      case "grey":
      default:
        return "card-tier-grey";
    }
  };

  const renderCardBody = () => (
    <div className={isCustomerPortal ? "claim-card-portal-content" : "claim-card-page"}>
      <div className="claim-card-container">
        {/* Header section */}
        <div className="claim-hero-header" >
          <span className="claim-hero-badge" style={{color:"white"}}>
            <MdQrCodeScanner /> TopCoin Rewards QR Card
          </span>
          <h1 className="claim-hero-title" style={{color:"white"}}>Claim Your TopCoin Reward</h1>
          <p className="claim-hero-subtitle" style={{color:"darkgray"}}>
            {isCustomerPortal
              ? "Verify your card details below and claim TopCoins directly into your wallet."
              : "Scan your TopCoin card QR code or enter credentials below to instantly add TopCoins into your customer wallet."}
          </p>
        </div>

              {/* Error Alert */}
              {error && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    padding: "14px 18px",
                    borderRadius: "12px",
                    marginBottom: "25px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <MdError style={{ fontSize: "22px", flexShrink: 0 }} />
                  <span>{error}</span>
                </div>
              )}

              {/* Success View */}
              {claimResult ? (
                <div className="claim-success-card">
                  <div className="claim-success-icon">
                    <MdCheckCircle />
                  </div>
                  <h2 className="claim-success-title">TopCoins Credited!</h2>
                  <p style={{ color: "#475569" }}>
                    {claimResult.message || "Your TopCoin card reward has been successfully transferred to your wallet."}
                  </p>

                  <div className="claim-success-amount">
                    +{claimResult.card?.top_coin_value || cardDetails?.top_coin_value} TopCoins
                  </div>

                  <div className="claim-success-receipt">
                    <div className="receipt-row">
                      <span>Card Number:</span>
                      <strong>{claimResult.card?.card_number || cardDetails?.card_number}</strong>
                    </div>
                    <div className="receipt-row">
                      <span>Card Tier:</span>
                      <strong>{claimResult.card?.card_tier || cardDetails?.card_tier}</strong>
                    </div>
                    <div className="receipt-row">
                      <span>Retail Shop:</span>
                      <strong>{cardDetails?.shop_name || "Assigned Retail Shop"}</strong>
                    </div>
                    {claimResult.transaction?.sbc_ref && (
                      <div className="receipt-row">
                        <span>Blockchain SBC Ref:</span>
                        <code style={{ fontSize: "11px", wordBreak: "break-all" }}>
                          {claimResult.transaction.sbc_ref}
                        </code>
                      </div>
                    )}
                    <div className="receipt-row">
                      <span>Redeemed At:</span>
                      <span>{claimResult.card?.redeemed_at || new Date().toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="claim-success-actions">
                    <Link to="/customer/wallet" className="btn-view-wallet">
                      <MdAccountBalanceWallet style={{ verticalAlign: "middle", marginRight: "6px" }} />
                      View My Wallet
                    </Link>
                    <Link to="/customer/qr-scan" className="btn-scan-more">
                      <MdQrCodeScanner style={{ verticalAlign: "middle", marginRight: "6px" }} />
                      Scan Another
                    </Link>
                  </div>
                </div>
              ) : (
                /* Card Layout */
                <div className="claim-card-layout">
                  {/* Left: 3D Digital Card Preview */}
                  <div>
                    <div
                      className={`digital-topcoin-card ${getTierClass(
                        cardDetails?.card_tier || "Grey"
                      )}`}
                      style={{
                        backgroundColor: cardDetails?.card_color || undefined,
                      }}
                    >
                      <div className="card-header-row">
                        <div className="card-header-logo">
                          <span>★ TOPCOIN UK</span>
                        </div>
                        <div className="card-chip"></div>
                        <span className="card-tier-badge">
                          {cardDetails?.card_tier || "Reward Card"}
                        </span>
                      </div>

                      <div className="card-body-row">
                        <div className="card-label">Reward Value</div>
                        <div className="card-value-display">
                          {cardDetails ? cardDetails.top_coin_value : "--"}
                          <span className="card-value-unit">TopCoins</span>
                        </div>
                      </div>

                      <div className="card-footer-row">
                        <div>
                          <div className="card-label">Card Number</div>
                          <div className="card-number">
                            {cardDetails?.card_number || cardNumber || "XXXX-0000"}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div className="card-label">Retailer</div>
                          <div className="card-shop-name">
                            {cardDetails?.shop_name || "TopCoin Partner"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "14px", color: "white", fontSize: "12px" }}>
                      Official TopCoin UK Retail Partner Rewards Card
                    </div>
                  </div>

                  {/* Right: Claim Details & Action */}
                  <div className="claim-panel">
                    <h3 className="claim-panel-title">Card Details</h3>

                    {loading ? (
                      <div style={{ textAlign: "center", padding: "40px 0", color: "#6f42c1" }}>
                        <div className="spinner" style={{ margin: "0 auto 15px" }}></div>
                        <p>Checking card details...</p>
                      </div>
                    ) : cardDetails ? (
                      <>
                        <div className="claim-info-list">
                          <div className="claim-info-item">
                            <span className="claim-info-label">Card Number</span>
                            <span className="claim-info-value" style={{ fontFamily: "monospace" }}>
                              {cardDetails.card_number}
                            </span>
                          </div>

                          <div className="claim-info-item">
                            <span className="claim-info-label">Reward Amount</span>
                            <span className="claim-info-value" style={{ color: "#16a34a", fontSize: "16px" }}>
                              +{cardDetails.top_coin_value} TopCoin
                            </span>
                          </div>

                          <div className="claim-info-item">
                            <span className="claim-info-label">Issued By Shop</span>
                            <span className="claim-info-value">
                              <MdStorefront style={{ verticalAlign: "middle", marginRight: "4px", color: "#6f42c1" }} />
                              {cardDetails.shop_name}
                            </span>
                          </div>

                          <div className="claim-info-item">
                            <span className="claim-info-label">Status</span>
                            <span
                              className={`status-badge ${
                                cardDetails.is_redeemed
                                  ? "redeemed"
                                  : cardDetails.is_assigned
                                  ? "active"
                                  : "unassigned"
                              }`}
                            >
                              {cardDetails.is_redeemed
                                ? "Already Redeemed"
                                : cardDetails.is_assigned
                                ? "Active & Ready"
                                : "Unassigned"}
                            </span>
                          </div>
                        </div>

                        {/* Action CTA */}
                        {cardDetails.is_redeemed ? (
                          <div
                            style={{
                              backgroundColor: "#fff1f2",
                              border: "1px solid #ffe4e6",
                              color: "#be123c",
                              padding: "14px",
                              borderRadius: "10px",
                              fontSize: "13px",
                              textAlign: "center",
                            }}
                          >
                            This card was already claimed
                            {cardDetails.redeemed_at ? ` on ${cardDetails.redeemed_at}` : ""}.
                          </div>
                        ) : !cardDetails.is_assigned ? (
                          <div
                            style={{
                              backgroundColor: "#fffbeb",
                              border: "1px solid #fef3c7",
                              color: "#b45309",
                              padding: "14px",
                              borderRadius: "10px",
                              fontSize: "13px",
                              textAlign: "center",
                            }}
                          >
                            This card has not yet been assigned to a participating shop.
                          </div>
                        ) : !isAuthenticated ? (
                          <div>
                            <div
                              style={{
                                backgroundColor: "#f0f9ff",
                                border: "1px solid #e0f2fe",
                                color: "#0369a1",
                                padding: "14px",
                                borderRadius: "10px",
                                fontSize: "13px",
                                marginBottom: "15px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <MdLock style={{ fontSize: "18px" }} />
                              <span>Log in with your TopCoin account to claim your reward.</span>
                            </div>
                            <button
                              onClick={() => {
                                sessionStorage.setItem(
                                  "claim_card_pending",
                                  JSON.stringify({ card: cardNumber, serial: serialNumber })
                                );
                                navigate("/sign_in#customer-login");
                              }}
                              className="claim-action-btn primary"
                            >
                              Log In to Claim {cardDetails.top_coin_value} TopCoins
                            </button>
                            <div style={{ textAlign: "center", marginTop: "12px", fontSize: "13px" }}>
                              New customer?{" "}
                              <Link to="/create-account" style={{ color: "#6f42c1", fontWeight: 600 }}>
                                Create free account
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={handleClaim}
                            disabled={claimLoading || !cardDetails.can_claim}
                            className="claim-action-btn primary"
                          >
                            {claimLoading ? (
                              "Processing Claim..."
                            ) : (
                              <>
                                <MdCheckCircle /> Claim {cardDetails.top_coin_value} TopCoins to Wallet
                              </>
                            )}
                          </button>
                        )}
                      </>
                    ) : (
                      <div style={{ color: "#64748b", fontSize: "14px", textAlign: "center", padding: "30px 10px" }}>
                        <p>No card loaded yet. Scan a QR code or enter your card details below.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Manual Lookup Option */}
              <div className="manual-lookup-box">
                <h4 className="manual-lookup-title">Enter Card Credentials Manually</h4>
                <form onSubmit={handleManualSearch} className="manual-lookup-grid">
                  <div className="manual-input-group">
                    <input
                      type="text"
                      placeholder="Card Number (e.g., GERT-0001)"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value.toUpperCase())}
                      required
                    />
                  </div>
                  <div className="manual-input-group">
                    <input
                      type="text"
                      placeholder="Serial Number (e.g., ABCD-EFGH-IJKL)"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value.toUpperCase())}
                    />
                  </div>
                  <button type="submit" disabled={loading} className="btn-lookup">
                    <MdSearch style={{ verticalAlign: "middle", marginRight: "4px" }} />
                    Verify Card
                  </button>
                </form>
              </div>
            </div>
          </div>
  );

  if (isCustomerPortal) {
    return (
      <div className="customer-claim-portal-wrapper" style={{ width: "100%", padding: "20px 24px 60px", maxWidth: "1050px", margin: "0 auto" }}>
        {renderCardBody()}
      </div>
    );
  }

  return (
    <Fragment>
      <div className="index_ico page_wrapper">
        <Header />
        <main className="page_content">
          <PageTitle pageTitle="Claim TopCoin Card" pagesub="Home" />
          {renderCardBody()}
        </main>
        <Footer />
        <Scrollbar />
      </div>
    </Fragment>
  );
};

export default ClaimTopCoinCard;
